import { useState } from 'react';
import { api, ApiError } from '../api.ts';
import { useSession } from '../session.tsx';
import { useRouter } from '../router.tsx';
import { Card, EmptyState, ErrorState, Field, Loading, Modal, Tag, useAsync, useDebounced, useToast } from '../ui.tsx';
import { STATUS_LABEL, STATUS_TONE, fmtDuration, fmtNumber, fmtPercent, relativeTime, fmtMoney } from '../format.ts';

interface VideoRow {
  id: string; public_id: string; title: string; description: string; status: string;
  published: 0 | 1; archived: 0 | 1; duration_sec: number | null; updated_at: string;
  project_id: string | null; thumbnail_ref: string | null; elements_count: number;
  asset: { detected_mime: string; size_bytes: number; codec_report: { browserSupport: string; videoCodecLabel: string | null } | null } | null;
  stats: {
    views: number; unique_viewers: number; plays: number; clicks: number; leads: number;
    purchases: number; revenue: number; completion_rate: number; ctr: number;
    conversion_rate: number; retention_at_50: number; cta_shown: number;
  };
}

interface Project { id: string; name: string; color: string; video_count: number; archived: 0 | 1 }

export function LibraryPage(): JSX.Element {
  const { workspace, canWrite, isDemo } = useSession();
  const { navigate } = useRouter();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [project, setProject] = useState('');
  const [status, setStatus] = useState('all');
  const [published, setPublished] = useState('all');
  const [archived, setArchived] = useState(false);
  const [sort, setSort] = useState('recent');
  const [showCreate, setShowCreate] = useState(false);
  const [showProjects, setShowProjects] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<VideoRow | null>(null);

  const debouncedSearch = useDebounced(search);

  const projects = useAsync(
    () => api<{ projects: Project[] }>('/api/projects'),
    [workspace?.id],
  );

  const videos = useAsync(() => {
    const query = new URLSearchParams();
    if (debouncedSearch) query.set('search', debouncedSearch);
    if (project) query.set('project', project);
    if (status !== 'all') query.set('status', status);
    if (published !== 'all') query.set('published', published);
    if (archived) query.set('archived', '1');
    query.set('sort', sort);
    return api<{ videos: VideoRow[]; total: number }>(`/api/videos?${query.toString()}`);
  }, [workspace?.id, debouncedSearch, project, status, published, archived, sort]);

  async function action(path: string, method: string, successMessage: string): Promise<void> {
    try {
      await api(path, { method });
      toast(successMessage, 'ok');
      videos.reload();
      projects.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha na operação.', 'error');
    }
  }

  const list = videos.data?.videos ?? [];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Biblioteca de vídeos</h1>
          <p className="page-sub">
            {videos.data ? `${fmtNumber(videos.data.total)} vídeo(s)` : 'Carregando…'}
            {isDemo && ' · workspace de demonstração (somente leitura)'}
          </p>
        </div>
        <div className="row tight">
          <button type="button" className="btn ghost" onClick={() => setShowProjects(true)}>Projetos</button>
          <button type="button" className="btn" disabled={!canWrite} onClick={() => setShowCreate(true)}>
            Novo vídeo
          </button>
        </div>
      </div>

      <Card className="filters" title="Busca e filtros">
        <div className="grid cols-4" style={{ alignItems: 'end' }}>
          <Field label="Buscar" id="q">
            <input id="q" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Título ou descrição" />
          </Field>
          <Field label="Projeto" id="proj">
            <select id="proj" value={project} onChange={(e) => setProject(e.target.value)}>
              <option value="">Todos os projetos</option>
              <option value="none">Sem projeto</option>
              {(projects.data?.projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Status" id="st">
            <select id="st" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="all">Todos</option>
              {Object.entries(STATUS_LABEL).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </Field>
          <Field label="Publicação" id="pub">
            <select id="pub" value={published} onChange={(e) => setPublished(e.target.value)}>
              <option value="all">Todos</option>
              <option value="published">Publicados</option>
              <option value="draft">Não publicados</option>
            </select>
          </Field>
        </div>
        <div className="row">
          <Field label="Ordenar" id="sort">
            <select id="sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="recent">Mais recentes</option>
              <option value="title">Título (A–Z)</option>
              <option value="views">Mais vistos</option>
            </select>
          </Field>
          <label className="checkline" style={{ marginTop: 12 }}>
            <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
            Mostrar arquivados
          </label>
        </div>
      </Card>

      <div style={{ height: 18 }} />

      {videos.loading && <Loading label="Carregando a biblioteca…" />}
      {videos.error && <ErrorState message={videos.error} onRetry={videos.reload} />}

      {!videos.loading && !videos.error && list.length === 0 && (
        <EmptyState
          title={archived ? 'Nenhum vídeo arquivado' : 'Sua biblioteca está vazia'}
          description={
            archived
              ? 'Vídeos arquivados aparecem aqui e podem ser restaurados a qualquer momento.'
              : 'Crie o primeiro vídeo, envie um MP4 (H.264/AAC), monte os CTAs na timeline e gere o embed.'
          }
          action={
            canWrite ? (
              <button type="button" className="btn" onClick={() => setShowCreate(true)}>Criar primeiro vídeo</button>
            ) : undefined
          }
        />
      )}

      <div className="video-grid">
        {list.map((video) => {
          const support = video.asset?.codec_report?.browserSupport;
          return (
            <article key={video.id} className="video-card">
              <div
                className="video-thumb"
                style={video.thumbnail_ref ? { backgroundImage: `url(/api/videos/${video.id}/thumb)` } : undefined}
              >
                {!video.thumbnail_ref && <span className="placeholder">sem thumbnail</span>}
                <div className="video-badges">
                  <Tag tone={STATUS_TONE[video.status] ?? 'muted'}>{STATUS_LABEL[video.status] ?? video.status}</Tag>
                  {video.published === 1 && <Tag tone="ok">Publicado</Tag>}
                  {video.archived === 1 && <Tag tone="muted">Arquivado</Tag>}
                  {support === 'unsupported' && <Tag tone="danger">Codec incompatível</Tag>}
                  {support === 'partial' && <Tag tone="warn">Suporte parcial</Tag>}
                </div>
              </div>

              <div className="video-body">
                <h3 className="video-name">{video.title}</h3>
                <p className="video-desc">{video.description || 'Sem descrição.'}</p>
                <div className="row tight" style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                  <span>{fmtDuration(video.duration_sec)}</span>
                  <span>·</span>
                  <span>{video.elements_count} elemento(s)</span>
                  <span>·</span>
                  <span>{relativeTime(video.updated_at)}</span>
                </div>

                <div className="video-metrics">
                  <div className="video-metric"><b>{fmtNumber(video.stats.views)}</b><span>views</span></div>
                  <div className="video-metric"><b>{fmtNumber(video.stats.plays)}</b><span>plays</span></div>
                  <div className="video-metric"><b>{fmtNumber(video.stats.clicks)}</b><span>cliques</span></div>
                  <div className="video-metric"><b>{fmtNumber(video.stats.purchases)}</b><span>compras</span></div>
                </div>
                <div className="video-metrics" style={{ borderTop: 0, paddingTop: 0 }}>
                  <div className="video-metric"><b>{fmtPercent(video.stats.retention_at_50, 0)}</b><span>ret. 50%</span></div>
                  <div className="video-metric"><b>{fmtPercent(video.stats.completion_rate, 0)}</b><span>conclusão</span></div>
                  <div className="video-metric"><b>{fmtPercent(video.stats.ctr, 1)}</b><span>CTR</span></div>
                  <div className="video-metric"><b>{fmtMoney(video.stats.revenue)}</b><span>receita</span></div>
                </div>
              </div>

              <div className="video-actions">
                <button type="button" className="btn small" onClick={() => navigate(`/app/videos/${video.id}`)}>Editar</button>
                {video.published === 1 && (
                  <a className="btn secondary small" href={`/v/${video.public_id}`} target="_blank" rel="noreferrer">Abrir player</a>
                )}
                <button
                  type="button" className="btn ghost small" disabled={!canWrite}
                  onClick={() => action(`/api/videos/${video.id}/duplicate`, 'POST', 'Vídeo duplicado.')}
                >
                  Duplicar
                </button>
                <button
                  type="button" className="btn ghost small" disabled={!canWrite}
                  onClick={() =>
                    action(
                      `/api/videos/${video.id}/${video.archived ? 'unarchive' : 'archive'}`,
                      'POST',
                      video.archived ? 'Vídeo restaurado.' : 'Vídeo arquivado.',
                    )
                  }
                >
                  {video.archived ? 'Restaurar' : 'Arquivar'}
                </button>
                <button type="button" className="btn ghost small" disabled={!canWrite} onClick={() => setConfirmDelete(video)}>
                  Excluir
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {showCreate && (
        <CreateVideoModal
          projects={projects.data?.projects ?? []}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => navigate(`/app/videos/${id}`)}
        />
      )}

      {showProjects && (
        <ProjectsModal
          projects={projects.data?.projects ?? []}
          canWrite={canWrite}
          onClose={() => { setShowProjects(false); projects.reload(); videos.reload(); }}
        />
      )}

      {confirmDelete && (
        <Modal
          title={`Excluir "${confirmDelete.title}"?`}
          onClose={() => setConfirmDelete(null)}
          footer={
            <>
              <button type="button" className="btn ghost" onClick={() => setConfirmDelete(null)}>Cancelar</button>
              <button
                type="button" className="btn danger"
                onClick={async () => {
                  const target = confirmDelete;
                  setConfirmDelete(null);
                  await action(`/api/videos/${target.id}`, 'DELETE', 'Vídeo excluído.');
                }}
              >
                Excluir definitivamente
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            O vídeo, o arquivo no Storage e os elementos interativos serão removidos. Os eventos já registrados
            permanecem no histórico para não distorcer relatórios passados.
          </p>
          <div className="alert warn">Esta ação não pode ser desfeita.</div>
        </Modal>
      )}
    </div>
  );
}

function CreateVideoModal({
  projects, onClose, onCreated,
}: { projects: Project[]; onClose: () => void; onCreated: (id: string) => void }): JSX.Element {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectId, setProjectId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const data = await api<{ video: { id: string } }>('/api/videos', {
        body: { title, description, projectId: projectId || undefined },
      });
      onCreated(data.video.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível criar o vídeo.');
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Novo vídeo"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn" onClick={submit} disabled={busy || title.trim().length < 1}>
            {busy ? <span className="spinner-inline" /> : null} Criar e enviar arquivo
          </button>
        </>
      }
    >
      <Field label="Título" id="t"><input id="t" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus /></Field>
      <Field label="Descrição" id="d">
        <textarea id="d" value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label="Projeto" id="p">
        <select id="p" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">Sem projeto</option>
          {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
        </select>
      </Field>
      {error && <div className="alert error">{error}</div>}
    </Modal>
  );
}

function ProjectsModal({
  projects, canWrite, onClose,
}: { projects: Project[]; canWrite: boolean; onClose: () => void }): JSX.Element {
  const toast = useToast();
  const [name, setName] = useState('');
  const [color, setColor] = useState('#a560f6');
  const [rows, setRows] = useState(projects);

  async function create(): Promise<void> {
    try {
      const data = await api<{ project: Project }>('/api/projects', { body: { name, color } });
      setRows([...rows, { ...data.project, video_count: 0 }]);
      setName('');
      toast('Projeto criado.', 'ok');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao criar projeto.', 'error');
    }
  }

  async function remove(id: string): Promise<void> {
    try {
      await api(`/api/projects/${id}`, { method: 'DELETE' });
      setRows(rows.filter((row) => row.id !== id));
      toast('Projeto excluído. Os vídeos ficaram sem projeto.', 'ok');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Falha ao excluir.', 'error');
    }
  }

  return (
    <Modal title="Projetos" onClose={onClose} footer={<button type="button" className="btn" onClick={onClose}>Fechar</button>}>
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <Field label="Novo projeto" id="np">
            <input id="np" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Campanha de lançamento" />
          </Field>
        </div>
        <div style={{ width: 74 }}>
          <Field label="Cor" id="nc"><input id="nc" type="color" value={color} onChange={(e) => setColor(e.target.value)} /></Field>
        </div>
        <button type="button" className="btn" style={{ marginBottom: 14 }} disabled={!canWrite || name.trim().length < 1} onClick={create}>
          Adicionar
        </button>
      </div>

      {rows.length === 0 ? (
        <p style={{ color: 'var(--text-3)' }}>Nenhum projeto ainda. Projetos agrupam vídeos de uma mesma campanha.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Projeto</th><th className="num">Vídeos</th><th /></tr></thead>
            <tbody>
              {rows.map((project) => (
                <tr key={project.id}>
                  <td>
                    <span className="row tight">
                      <i className="dot" style={{ background: project.color }} />
                      {project.name}
                    </span>
                  </td>
                  <td className="num">{project.video_count}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button type="button" className="btn ghost small" disabled={!canWrite} onClick={() => remove(project.id)}>
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
